import { format } from "date-fns";
import { vi } from "date-fns/locale";

/**
 * Làm sạch tên công đoạn, triệt tiêu hoàn toàn mã code kỹ thuật thô và duplicate
 * Tuân thủ Rule 6 (Clean Lookups & Anti-Duplicate)
 */
export function cleanProcessName(name?: string | null, stepId?: string | null, groupName?: string | null): string {
  if (!name && !stepId) return "Công đoạn";
  const raw = (name || stepId || "").trim();

  // 1. Loại bỏ các tiền tố mã code thô dạng "PROC-IN-OFFSET: ..." hoặc "PROC_..."
  let cleaned = raw.replace(/^PROC[-_][A-Z0-9-_]+[:\s-]+/i, "").trim();
  cleaned = cleaned.replace(/^STEP[-_][A-Z0-9-_]+[:\s-]+/i, "").trim();

  // 2. Chuyển đổi các mã snake_case uppercase thô sang tiếng Việt chuẩn
  const codeDictionary: Record<string, string> = {
    PRINTING: "In Offset",
    IN_OFFSET: "In Offset",
    OFFSET: "In Offset",
    CUTTING: "Cắt Xén Phôi",
    CAT_XEN: "Cắt Xén Phôi",
    LAMINATING: "Cán Màng",
    CAN_MANG: "Cán Màng",
    CAN_MANG_BONG: "Cán Màng Bóng",
    CAN_MANG_MO: "Cán Màng Mờ",
    DIE_CUTTING: "Bế Hộp",
    BE: "Bế Hộp",
    BE_HOP: "Bế Hộp",
    BE_THIET_KE: "Bế Thành Phẩm",
    GLUING: "Dán Hộp",
    DAN_HOP: "Dán Cạnh Đáy",
    COATING: "Tráng Phủ / UV",
    TRANG_PHU: "Tráng Phủ / UV",
    PACKING: "Đóng Gói / KCS",
    DONG_GOI: "Đóng Gói / KCS",
    KCS: "KCS / Kiểm Phẩm",
    QC: "KCS / Kiểm Phẩm",
    EP_KIM: "Ép Kim / Nhũ",
    DAN_CUA_SO: "Dán Cửa Sổ",
  };

  const upperCode = cleaned.toUpperCase().replace(/\s+/g, "_");
  if (codeDictionary[upperCode]) {
    return codeDictionary[upperCode];
  }

  // 3. Nếu tên bắt đầu bằng "PROC-" thô mà không có chữ tiếng Việt
  if (/^PROC[-_]/i.test(cleaned)) {
    const matched = Object.keys(codeDictionary).find((k) => upperCode.includes(k));
    if (matched) return codeDictionary[matched];
    if (groupName && !/^PROC[-_]/i.test(groupName)) return groupName;
  }

  return cleaned || "Công đoạn";
}

/**
 * Format hiển thị thời gian định mức làm việc gọn gàng, chuẩn xác
 */
export function formatProcessDuration(
  hours?: number | null,
  totalMinutes?: number | null,
  setupMinutes?: number | null,
  speed?: number | null
): string {
  let minutes = totalMinutes || 0;
  if (!minutes && hours) {
    minutes = Math.round(Number(hours) * 60);
  }

  let durationText = "—";
  if (minutes > 0) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) {
      durationText = `${h}h ${m}p`;
    } else if (h > 0) {
      durationText = `${h}h`;
    } else {
      durationText = `${m}p`;
    }
  }

  if (speed && speed > 0) {
    const formattedSpeed = Number(speed).toLocaleString("vi-VN");
    return durationText !== "—" ? `${durationText} • ${formattedSpeed} sp/h` : `${formattedSpeed} sp/h`;
  }

  return durationText;
}

export interface DailySubtotal {
  totalStandardQty: number;
  totalActualOk: number;
  totalActualNg: number;
  totalRemainingQty: number;
  totalMinutes: number;
  totalDurationHours: number;
  progressPercent: number;
  itemCount: number;
}

export interface DayGroup {
  dateKey: string; // YYYY-MM-DD or "NO_DATE"
  displayDate: string; // "Thứ Năm, 08/10/2026" or "Chờ xếp lịch"
  rawDate: Date | null;
  items: any[];
  subtotal: DailySubtotal;
}

/**
 * Tính toán Subtotal tổng kết cho một danh sách công đoạn (tuân thủ Rule 11)
 */
export function calculateDailySubtotal(items: any[]): DailySubtotal {
  let totalStandardQty = 0;
  let totalActualOk = 0;
  let totalActualNg = 0;
  let totalRemainingQty = 0;
  let totalMinutes = 0;

  for (const item of items) {
    const std = Number(item.standardQuantity) || 0;
    const ok = Number(item.actualQtyOk) || 0;
    const ng = Number(item.actualQtyNg) || 0;
    const rem = Number(item.remainingQty) || Math.max(0, std - ok);

    totalStandardQty += std;
    totalActualOk += ok;
    totalActualNg += ng;
    totalRemainingQty += rem;

    let mins = Number(item.plannedTotalTime) || 0;
    if (!mins && item.estDurationHours) {
      mins = Math.round(Number(item.estDurationHours) * 60);
    }
    totalMinutes += mins;
  }

  const progressPercent =
    totalStandardQty > 0 ? Math.min(100, Math.round((totalActualOk / totalStandardQty) * 100)) : 0;

  return {
    totalStandardQty,
    totalActualOk,
    totalActualNg,
    totalRemainingQty,
    totalMinutes,
    totalDurationHours: Math.round((totalMinutes / 60) * 10) / 10,
    progressPercent,
    itemCount: items.length,
  };
}

/**
 * Phân nhóm công đoạn theo ngày triển khai (plannedStartDate), sắp xếp desc
 */
export function groupStepsByDate(items: any[]): DayGroup[] {
  const groupsMap = new Map<string, any[]>();
  const noDateItems: any[] = [];

  for (const item of items) {
    const dateStr = item.plannedStartDate || item.scheduledStartTime;
    if (!dateStr) {
      noDateItems.push(item);
      continue;
    }

    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) {
        noDateItems.push(item);
        continue;
      }
      const key = format(d, "yyyy-MM-dd");
      if (!groupsMap.has(key)) {
        groupsMap.set(key, []);
      }
      groupsMap.get(key)!.push(item);
    } catch {
      noDateItems.push(item);
    }
  }

  // Sắp xếp các ngày giảm dần (desc)
  const sortedDates = Array.from(groupsMap.keys()).sort((a, b) => b.localeCompare(a));

  const result: DayGroup[] = sortedDates.map((dateKey) => {
    const groupItems = groupsMap.get(dateKey)!;
    const firstDate = new Date(groupItems[0].plannedStartDate || groupItems[0].scheduledStartTime);
    let displayDate = dateKey;
    try {
      // Ví dụ: Thứ Năm, 08/10/2026
      const dayName = format(firstDate, "EEEE", { locale: vi });
      const dayFormatted = dayName.charAt(0).toUpperCase() + dayName.slice(1);
      displayDate = `${dayFormatted}, ${format(firstDate, "dd/MM/yyyy")}`;
    } catch {
      displayDate = dateKey;
    }

    return {
      dateKey,
      displayDate,
      rawDate: firstDate,
      items: groupItems,
      subtotal: calculateDailySubtotal(groupItems),
    };
  });

  // Nếu có công đoạn chưa có ngày, đặt vào nhóm cuối cùng
  if (noDateItems.length > 0) {
    result.push({
      dateKey: "NO_DATE",
      displayDate: "Chưa Có Lịch Bắt Đầu (Chờ Xếp Lịch)",
      rawDate: null,
      items: noDateItems,
      subtotal: calculateDailySubtotal(noDateItems),
    });
  }

  return result;
}

export interface MachineGroup {
  machineId: string;
  machineName: string;
  machineCode?: string;
  items: any[];
  totalMinutes: number;
  totalDurationHours: number;
  totalQty: number;
}

/**
 * Phân nhóm công đoạn theo máy móc phục vụ giao diện Kanban
 */
export function groupStepsByMachine(items: any[], allMachines: any[] = []): MachineGroup[] {
  const machineMap = new Map<string, any[]>();
  const unassigned: any[] = [];

  for (const item of items) {
    const mId = item.machineId || item.resourceId;
    if (!mId) {
      unassigned.push(item);
      continue;
    }
    if (!machineMap.has(mId)) {
      machineMap.set(mId, []);
    }
    machineMap.get(mId)!.push(item);
  }

  const result: MachineGroup[] = [];

  // Tạo cột cho các máy có công đoạn hoặc danh sách máy master
  const machineInfoMap = new Map<string, any>(allMachines.map((m) => [m.id, m]));

  // 1. Các máy có công đoạn
  machineMap.forEach((mItems, mId) => {
    const info = machineInfoMap.get(mId);
    let totalMins = 0;
    let totalQty = 0;
    mItems.forEach((it) => {
      let m = Number(it.plannedTotalTime) || 0;
      if (!m && it.estDurationHours) m = Math.round(Number(it.estDurationHours) * 60);
      totalMins += m;
      totalQty += Number(it.standardQuantity) || 0;
    });

    result.push({
      machineId: mId,
      machineName: info?.name || mItems[0]?.machineName || `Máy #${mId.slice(-4)}`,
      machineCode: info?.machineId || mItems[0]?.machineCode || "",
      items: mItems,
      totalMinutes: totalMins,
      totalDurationHours: Math.round((totalMins / 60) * 10) / 10,
      totalQty,
    });
  });

  // 2. Cột Chưa Phân Máy (đặt lên đầu nếu có)
  if (unassigned.length > 0) {
    let unassignedMins = 0;
    let unassignedQty = 0;
    unassigned.forEach((it) => {
      let m = Number(it.plannedTotalTime) || 0;
      if (!m && it.estDurationHours) m = Math.round(Number(it.estDurationHours) * 60);
      unassignedMins += m;
      unassignedQty += Number(it.standardQuantity) || 0;
    });

    result.unshift({
      machineId: "UNASSIGNED",
      machineName: "Chưa Phân Bổ Máy",
      machineCode: "PENDING",
      items: unassigned,
      totalMinutes: unassignedMins,
      totalDurationHours: Math.round((unassignedMins / 60) * 10) / 10,
      totalQty: unassignedQty,
    });
  }

  return result;
}
